import { getLaitenettiCardContent } from '../../components/OrderSubscriptionSelection/content/LaitenettiSubscriptionCardContent';
import { getMobileBroadbandSubscriptionCardContents } from '../../components/OrderSubscriptionSelection/content/MobileBroadbandSubscriptionCardContent';
import { getVoiceSubscriptionCardContents } from '../../components/OrderSubscriptionSelection/content/VoiceSubscriptionCardContent';
import { FIVE_G_KEY } from '../../components/ProductGrid/ProductGridFilter';
import { CommercialProductSubType } from '../../generated/api/commercialProductSubType';
import { CommercialProductType } from '../../generated/api/commercialProductType';
import { AnalyticsItemCategory, CURRENCY_CODE_EUR, EcommerceEventTypeV4 } from '../analytics';
import { MobileM2MAddonCode } from '../enums';
import { getEffectiveCharges } from './addOnUtils';
import { getLaitenettiMonthlyPriceFromCommercialProduct } from './commercialProductUtils';
import { centsToEuros } from './priceUtils';
import { isFiveGPlusOffer, isLaitenettiOffer } from './subscriptionUtils';
import { VAT_AS_DECIMAL } from './taxUtils';
import type { AnalyticsPrices } from '../../components/HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils';
import type { Filters } from '../../components/ProductGrid/ProductGridFilter';
import type { SortType } from '../../components/ProductGrid/ProductGridSort';
import type {
  MobileBroadbandSubCardProps,
  SubscriptionCardProps,
} from '../../components/SubscriptionCard/SubscriptionCard';
import type { AddOn } from '../../generated/api/addOn';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation';
import type { CommercialProduct } from '../../generated/api/commercialProduct';
import type { Offer } from '../../generated/api/offer';
import type { Subscription } from '../../generated/api/subscription';
import type {
  EcommerceAddToCartEventV4,
  EcommerceItem,
  EcommercePurchaseEventV4,
  EcommerceRemoveFromCartEventV4,
} from '../analytics';
import type { AddedAddon } from '../types/addon';
import type { FilterEvent, SortEvent } from '../types/analytics';
import type { ConfiguredCommercialProduct, ConfiguredOffer } from '../types/commercialProduct';

// Max bundle ID length in analytics backend
const BUNDLE_ID_MAX_LENGTH = 500;

export const ADDON_ANALYTICS_ITEM_CATEGORY = 'Additional Services';
export const DEFAULT_ITEM_BRAND = 'Elisa';
export const DEFAULT_PAYMENT_TYPE = 'invoice';

export enum AnalyticsListNames {
  CATEGORY_LIST_LIST_NAME = 'Category list (tuotelistaus)',
  LAITE_NETTI_LIST_NAME = 'Laitenettiliittymät',
  MBB_YRITYSDATA_LIST_NAME = 'Laajakaistaliittymät Yritysdata',
  MBB_LIIKUVA_WIFI_LIST_NAME = 'Laajakaistaliittymät Liikkuva Wifi',
  MINUTE_BASED_LIST_NAME = 'Minuuttihintaiset liittymät',
  NETTI_LITE_LIST_NAME = 'Nettilite saatavuus',
  PROMOTION_LIST_NAME = 'Promotion block (tuotenosto)',
  VOICE_LIST_NAME = 'Puhelinliittymät',
}

export enum AnalyticsEventOrigins {
  EMPLOYEE = 'Työntekijän OmaElisa',
  FROSMO = 'Frosmo',
  OMAELISA = 'OmaElisa',
  PUBLIC = 'Julkinen',
  PUNCHOUT = 'Punchout',
}

export enum AddOnsGroups {
  LAITE_NETTI = 'Internet',
}

// We have rather limited information about the products available at purchase stage,
//  so we have to use the product name to decide whether it's a sub or not.
const subscriptionProductCategories = [
  AnalyticsItemCategory.YRITYS_DATA_LIITTYMA,
  AnalyticsItemCategory.YRITYS_LIITTYMA,
  AnalyticsItemCategory.LAITE_NETTI,
  AnalyticsItemCategory.LIIKKUVA_WIFI,
  AnalyticsItemCategory.SALES_PRODUCT,
];

export const isSubscriptionProduct = (productName?: string) =>
  subscriptionProductCategories.some(name => productName?.includes(name.toString()));

export const getBundleId = (commercialProductCode: string, addedAddOns?: AddedAddon[]): string | undefined => {
  // Only assign a bundle ID to orders with more than one unique item (= has to have addons)
  if (!addedAddOns || addedAddOns.length === 0) {
    return undefined;
  }
  if (addedAddOns && addedAddOns.length > 0) {
    const addOnString = addedAddOns?.map(addOn => addOn.addOnCode).toString();
    // If there is huge pile of addons included, the bundle ID will be truncated to max length 500 characters
    return (commercialProductCode + addOnString).substring(0, BUNDLE_ID_MAX_LENGTH);
  }
  return commercialProductCode;
};

const getCommercialProductOneTimeChargeInCents = (
  commercialProducts?: CommercialProduct[],
  campaignAssociation?: CampaignAssociation,
  isLaitenetti?: boolean | undefined,
  analyticsPrices?: AnalyticsPrices | undefined
) => {
  const totalPrice = commercialProducts?.reduce((total, commercialProduct) => {
    // Calculate the net one-time charge after applying the campaign discount
    const oneTimeCharge = (() => {
      if (isLaitenetti) {
        return analyticsPrices?.oneTimePrice ?? commercialProduct?.oneTimeCharge ?? 0;
      } else {
        return commercialProduct?.oneTimeCharge ?? 0;
      }
    })();

    const oneTimeDiscount = campaignAssociation?.oneTimeCharge ?? 0;
    const netOneTimeCharge = oneTimeCharge - oneTimeDiscount;

    // Calculate the total price including the one-time charge
    return total + netOneTimeCharge;
  }, 0);

  return totalPrice ?? 0;
};

const getCommercialProductYearlyPriceWithoutOneTimeChargeInCents = (
  commercialProducts?: CommercialProduct[],
  campaignAssociation?: CampaignAssociation,
  isLaitenetti?: boolean
) => {
  const totalPrice = commercialProducts?.reduce((total, commercialProduct) => {
    const recurringCharge = isLaitenetti
      ? getLaitenettiMonthlyPriceFromCommercialProduct(commercialProduct)
      : getEffectiveCharges(commercialProduct).monthlyRecurringCharge;
    const monthlyDiscount = campaignAssociation?.monthlyRecurringCharge ?? 0;

    // Calculate the net charge after applying the campaign discount
    const netCharge = recurringCharge - monthlyDiscount;

    // billing period in months
    const billingPeriod = commercialProduct.billingPeriod || 1;

    // Calculate the total yearly charge
    const yearlyRecurringCharge = netCharge * (12 / billingPeriod);

    return total + yearlyRecurringCharge;
  }, 0);

  return totalPrice ?? 0;
};

export const getCommercialProductYearlyPrice = (
  commercialProducts?: CommercialProduct[],
  campaignAssociation?: CampaignAssociation,
  isLaitenetti?: boolean,
  analyticsPrices?: AnalyticsPrices | undefined
) => {
  return centsToEuros(
    getCommercialProductOneTimeChargeInCents(commercialProducts, campaignAssociation, isLaitenetti, analyticsPrices) +
      getCommercialProductYearlyPriceWithoutOneTimeChargeInCents(commercialProducts, campaignAssociation, isLaitenetti)
  );
};

export const resolveECommerceEventListName = (type: string, eventOrigin: string): string => {
  switch (type) {
    case 'CategoryList':
      return `${eventOrigin} ${AnalyticsListNames.CATEGORY_LIST_LIST_NAME}`;
    case 'PromotionBlock':
      return `${eventOrigin} ${AnalyticsListNames.PROMOTION_LIST_NAME}`;
    default:
      throw new Error(`Unidentified ecommerce event key: ${type}`);
  }
};

export const getAddOnYearlyPrice = (addOn: AddedAddon) => {
  const monthlyRecurringCharge = addOn?.monthlyPrice || 0;
  const oneTimeCharge = addOn?.oneTimePrice || 0;

  // Calculate the total yearly charge before quantity and one-time charges
  const yearlyRecurringCharge = monthlyRecurringCharge * 12;

  // Calculate the total price including the one-time charge and quantity
  const totalPrice = yearlyRecurringCharge + oneTimeCharge;

  return centsToEuros(totalPrice);
};

/** Calculates cumulative price from ecommerce items (price * quantity). */
export const getCumulativePrice = (eCommerceItems: EcommerceItem[]) =>
  eCommerceItems.reduce((total, item) => total + (item.price ?? 0) * (item.quantity ?? 1), 0);

/** Calculates tax for analytics events (price is in euros, returns 2 decimal places). */
export const calculateAnalyticsTax = (priceInEuros: number) => parseFloat((priceInEuros * VAT_AS_DECIMAL).toFixed(2));

const getSalesProductItemCategory = (onlineModelName: string): AnalyticsItemCategory => {
  if (
    onlineModelName.includes(AnalyticsItemCategory.YRITYS_DATA_LIITTYMA) ||
    onlineModelName.includes(AnalyticsItemCategory.LIIKKUVA_WIFI)
  ) {
    return AnalyticsItemCategory.YRITYS_DATA_LIITTYMA;
  }
  if (onlineModelName.includes(AnalyticsItemCategory.YRITYS_LIITTYMA)) {
    return AnalyticsItemCategory.YRITYS_LIITTYMA;
  }
  if (onlineModelName.includes(AnalyticsItemCategory.LAITE_NETTI)) {
    return AnalyticsItemCategory.LAITE_NETTI;
  }
  return AnalyticsItemCategory.UNKNOWN_CATEGORY;
};

export const getItemCategory = (
  onlineModelName: string,
  productType?: CommercialProductType,
  productSubType?: CommercialProductSubType,
  itemAnalyticsListName?: string
): AnalyticsItemCategory => {
  // TODO if the data is coming from site-backend, productType is incorrectly set to HANDHELD_DEVICE for sales products.
  // This is a workaround to handle that case and can be removed after https://atlas.elisa.fi/jira/browse/OFI-58170 is fixed.
  if (productType === CommercialProductType.SALES_PRODUCT || productType === CommercialProductType.HANDHELD_DEVICE) {
    return getSalesProductItemCategory(onlineModelName);
  }

  if (onlineModelName.includes(AnalyticsItemCategory.LAITE_NETTI)) {
    return AnalyticsItemCategory.LAITE_NETTI;
  }
  if (itemAnalyticsListName === AnalyticsListNames.NETTI_LITE_LIST_NAME) {
    return AnalyticsItemCategory.FIXED_BROADBAND;
  }

  switch (productSubType) {
    case CommercialProductSubType.MOBILE_VOICE:
      return AnalyticsItemCategory.YRITYS_LIITTYMA;
    case CommercialProductSubType.MOBILE_BROADBAND:
      return AnalyticsItemCategory.YRITYS_DATA_LIITTYMA;
    case CommercialProductSubType.M2M:
      return AnalyticsItemCategory.LAITE_NETTI;
    default:
      return AnalyticsItemCategory.UNKNOWN_CATEGORY;
  }
};

// Laitenetti card is a special case, the offer is same in all cards and the selectedAddon is used to determine the card
const getLaitenettiCard = (addons?: AddOn[]): SubscriptionCardProps | undefined => {
  const laiteNettiCards = getLaitenettiCardContent(false);

  // Find the first card that has a selectedAddon matching any addon code in the addons array. Filter out addOnGroups
  // other than Internet
  return laiteNettiCards.find(card =>
    addons
      ?.filter(addon => addon.addOnGroup === AddOnsGroups.LAITE_NETTI)
      .some(addon => card.selectedAddon === addon.addOnCode)
  );
};

export const getLaitenettiCardByMainHeader = (name: string): SubscriptionCardProps | undefined => {
  const laiteNettiCards = getLaitenettiCardContent(false);
  return laiteNettiCards.find(card => card.mainHeader === name);
};

const getNonLaitenettiCard = (offerCode: string) => {
  const voiceCards = getVoiceSubscriptionCardContents(false);
  const mbbCards = getMobileBroadbandSubscriptionCardContents(false);

  return [...voiceCards, ...mbbCards].find(
    card =>
      card.selectedOffer === offerCode ||
      ('offers' in card && card.offers?.some((offer: { guid: string }) => offer.guid === offerCode))
  );
};

const isSubscriptionCardProps = (
  card: SubscriptionCardProps | MobileBroadbandSubCardProps
): card is SubscriptionCardProps => 'offers' in card;

const isMobileBroadbandSubCardProps = (
  card: SubscriptionCardProps | MobileBroadbandSubCardProps
): card is MobileBroadbandSubCardProps => 'salesProduct' in card;

const getPublicPageCard = (
  offerCode: string,
  analyticsItemCategory?: AnalyticsItemCategory
): SubscriptionCardProps | MobileBroadbandSubCardProps | undefined => {
  const voiceCards = getVoiceSubscriptionCardContents(true);
  const mbbCards = getMobileBroadbandSubscriptionCardContents(true);
  const laiteNettiCards = getLaitenettiCardContent(false);

  return [...voiceCards, ...mbbCards, ...laiteNettiCards].find(card => {
    if (analyticsItemCategory === AnalyticsItemCategory.YRITYS_LIITTYMA) {
      return (
        isSubscriptionCardProps(card) &&
        card.offers?.some(
          offer =>
            offer.salesProduct?.newNumberOfferCode === offerCode || offer.salesProduct?.oldNumberOfferCode === offerCode
        )
      );
    }
    return isMobileBroadbandSubCardProps(card) && card.publicPage?.offerCode === offerCode;
  });
};

// TODO can be removed after https://atlas.elisa.fi/jira/browse/OFI-58170 is fixed
const isSalesProduct = (commercialProduct?: CommercialProduct) =>
  commercialProduct?.productType === CommercialProductType.SALES_PRODUCT ||
  commercialProduct?.productType === CommercialProductType.HANDHELD_DEVICE;

export const getSubscriptionCardByOffer = (
  offer: Offer,
  commercialProduct?: CommercialProduct,
  analyticsItemCategory?: AnalyticsItemCategory
): SubscriptionCardProps | MobileBroadbandSubCardProps | undefined => {
  const { offerCode } = offer;

  if (isSalesProduct(commercialProduct)) {
    return getPublicPageCard(offerCode, analyticsItemCategory);
  }

  return isLaitenettiOffer(offer)
    ? getLaitenettiCard(commercialProduct?.associatedAddOns)
    : getNonLaitenettiCard(offerCode);
};

const getVariantSuffixForMultipleOffers = (hasMultipleOffers: boolean, offer: Offer): string => {
  if (hasMultipleOffers && offer.commercialProducts[0]?.commercialProductName) {
    return ` (${offer.commercialProducts[0].commercialProductName})`;
  }
  return '';
};

export const getLaitenettiProductItemVariant = (
  offer: Offer,
  subscriptionCard?: SubscriptionCardProps | MobileBroadbandSubCardProps,
  selectedCommercialProduct?: ConfiguredCommercialProduct[]
): string => {
  const prefix = DEFAULT_ITEM_BRAND;
  if (subscriptionCard) {
    const itemName = offer.commercialProducts[0]?.commercialProductName ?? subscriptionCard.mainHeader;
    return itemName.startsWith(prefix) ? itemName : `${prefix} ${itemName}`;
  }

  const cpName =
    offer.commercialProducts[0]?.commercialProductName ??
    selectedCommercialProduct?.[0].commercialProduct.commercialProductName ??
    '';
  return cpName.startsWith(prefix) ? cpName : `${prefix} ${cpName}`;
};

export const getProductItemVariant = (
  itemCategory: AnalyticsItemCategory,
  offer: Offer,
  subscriptionCard?: SubscriptionCardProps | MobileBroadbandSubCardProps,
  selectedCommercialProduct?: ConfiguredCommercialProduct[]
): string => {
  const fiveGPlusOffer = isFiveGPlusOffer(offer.offerCode) ? '5G+ ' : '';
  if (subscriptionCard) {
    if (itemCategory === AnalyticsItemCategory.LAITE_NETTI) {
      return getLaitenettiProductItemVariant(offer, subscriptionCard, selectedCommercialProduct);
    }
    const offers = 'offers' in subscriptionCard ? (subscriptionCard as SubscriptionCardProps).offers : undefined;
    const hasMultipleOffers = (offers?.length ?? 0) > 1;
    const selectedOfferText = hasMultipleOffers ? offers?.find(o => o.guid === offer.offerCode)?.text : undefined;
    const variantSuffix = selectedOfferText
      ? ` (${selectedOfferText})`
      : getVariantSuffixForMultipleOffers(hasMultipleOffers, offer);
    return `${DEFAULT_ITEM_BRAND} ${itemCategory} ${fiveGPlusOffer}${subscriptionCard.mainHeader}${variantSuffix}`;
  }
  const cpName =
    offer.commercialProducts[0]?.commercialProductName ??
    selectedCommercialProduct?.[0].commercialProduct.commercialProductName ??
    '';
  return `${DEFAULT_ITEM_BRAND} ${itemCategory} ${fiveGPlusOffer}${cpName}`;
};

export const getCommercialProductToUse = (offer: Offer, selectedCommercialProducts?: ConfiguredCommercialProduct[]) => {
  // Offer coming from /kassa might not include commercial products
  const cpsToUse: CommercialProduct[] = [
    ...offer.commercialProducts,
    ...(selectedCommercialProducts?.map(cp => cp.commercialProduct) ?? []),
  ];
  const isSalesProduct2 = cpsToUse[0]?.productType === CommercialProductType.SALES_PRODUCT;
  const spCpWithMonthlyRecurringCharge = cpsToUse.find(cp => getEffectiveCharges(cp).monthlyRecurringCharge > 0);
  return isSalesProduct2 && spCpWithMonthlyRecurringCharge ? spCpWithMonthlyRecurringCharge : cpsToUse[0];
};

export const getEcommerceItemFromOffer = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  index?: number,
  coupon?: string,
  campaignAssociation?: CampaignAssociation,
  itemListName?: string,
  selectedCommercialProducts?: ConfiguredCommercialProduct[]
): EcommerceItem => {
  const cpToUse = getCommercialProductToUse(offer, selectedCommercialProducts);
  const itemCategory = getItemCategory(onlineModelName, cpToUse.productType, cpToUse.productSubType, itemListName);
  const subscriptionCard = getSubscriptionCardByOffer(offer, cpToUse, itemCategory);

  const commercialProducts = offer.commercialProducts.length
    ? offer.commercialProducts
    : selectedCommercialProducts?.map(cp => cp.commercialProduct);
  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    ...(coupon != null ? { coupon } : {}),
    ...(index != null ? { index } : {}),
    item_brand: DEFAULT_ITEM_BRAND,
    item_category: itemCategory,
    item_id:
      itemCategory === AnalyticsItemCategory.LAITE_NETTI
        ? subscriptionCard?.selectedAddon || cpToUse.commercialProductCode // Should always exist in Laitenetti
        : cpToUse.commercialProductCode,
    ...(itemListName != null ? { item_list_name: itemListName } : {}),
    item_name: subscriptionCard?.mainHeader || cpToUse.commercialProductName,
    item_variant: getProductItemVariant(itemCategory, offer, subscriptionCard, selectedCommercialProducts),
    price: getCommercialProductYearlyPrice(commercialProducts, campaignAssociation, isLaitenettiOffer(offer)),
    quantity,
  };
};

export const getItemVariant = (eventType: EcommerceEventTypeV4, commercialProductName: string): string | undefined =>
  eventType !== EcommerceEventTypeV4.SELECT_ITEM && eventType !== EcommerceEventTypeV4.VIEW_ITEM_LIST
    ? commercialProductName
    : undefined;

export const createSortEvent = (sortType: SortType): SortEvent => {
  return {
    event: 'apply_sort',
    sort: sortType.valueOf(),
  };
};

export const createFilterEvent = (filters: Filters): FilterEvent => {
  return {
    event: 'apply_filter',
    filter: {
      brand: filters.FILTER_MANUFACTURER.selectedOptions,
      price: filters.FILTER_COST.selectedOptions,
      os: [...filters.FILTER_OS_COMPUTERS.selectedOptions, ...filters.FILTER_OS_PHONES.selectedOptions],
      fiveG: filters.FILTER_5G.selectedOptions.indexOf(FIVE_G_KEY) !== -1,
      offers: filters.FILTER_OFFERS.selectedOptions.length !== 0,
    },
  };
};

export const getEcommerceItemsFromAddedAddonOns = (
  quantity: number,
  eventType: EcommerceEventTypeV4,
  addOns?: AddedAddon[],
  bundleId?: string
): EcommerceItem[] =>
  addOns
    // Laitenetti is a special case which includes addon by default. However, addon should not create a separate ecommerce item.
    ?.filter(ao => !Object.values(MobileM2MAddonCode).includes(ao.addOnCode as MobileM2MAddonCode))
    ?.map(addOn => ({
      item_brand: DEFAULT_ITEM_BRAND,
      ...(bundleId != null ? { item_bundle_id: bundleId } : {}),
      item_category: ADDON_ANALYTICS_ITEM_CATEGORY,
      item_id: addOn.addOnCode,
      item_name: addOn.displayName,
      item_variant: getItemVariant(eventType, addOn.displayName),
      price: getAddOnYearlyPrice(addOn),
      quantity,
    })) || [];

export const getEcommerceItems = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  eventType: EcommerceEventTypeV4,
  addedAddOns?: AddedAddon[],
  coupon?: string,
  campaignAssociation?: CampaignAssociation,
  selectedCommercialProducts?: ConfiguredCommercialProduct[]
): EcommerceItem[] => {
  // If coming from /kassa, offer might not include commercial products
  const cpsToUse: CommercialProduct[] = [
    ...offer.commercialProducts,
    ...(selectedCommercialProducts?.map(cp => cp.commercialProduct) ?? []),
  ];
  const firstCp = cpsToUse[0];
  const isSalesProduct2 = firstCp.productType === CommercialProductType.SALES_PRODUCT;
  const spCpWithMonthlyRecurringCharge = cpsToUse.find(cp => getEffectiveCharges(cp).monthlyRecurringCharge > 0);
  const bundleId = getBundleId(
    isSalesProduct2 && spCpWithMonthlyRecurringCharge
      ? spCpWithMonthlyRecurringCharge.commercialProductCode
      : firstCp?.commercialProductCode,
    addedAddOns
  );

  return [
    {
      ...getEcommerceItemFromOffer(
        onlineModelName,
        offer,
        quantity,
        undefined,
        coupon,
        campaignAssociation,
        undefined,
        selectedCommercialProducts
      ),
      ...(bundleId != null ? { item_bundle_id: bundleId } : {}),
    },
    ...getEcommerceItemsFromAddedAddonOns(quantity, eventType, addedAddOns, bundleId),
  ];
};

export const getAddToCartAnalyticsEvent = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  addedAddOns?: AddedAddon[],
  voucher?: string,
  campaignAssociation?: CampaignAssociation
): EcommerceAddToCartEventV4 => ({
  event: EcommerceEventTypeV4.ADD_TO_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: getEcommerceItems(
      onlineModelName,
      offer,
      quantity,
      EcommerceEventTypeV4.ADD_TO_CART,
      addedAddOns,
      voucher,
      campaignAssociation
    ),
  },
});

export const getRemoveFromCartAnalyticsEvent = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  addedAddOns?: AddedAddon[],
  voucher?: string,
  campaignAssociation?: CampaignAssociation
): EcommerceRemoveFromCartEventV4 => ({
  event: EcommerceEventTypeV4.REMOVE_FROM_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: getEcommerceItems(
      onlineModelName,
      offer,
      quantity,
      EcommerceEventTypeV4.REMOVE_FROM_CART,
      addedAddOns,
      voucher,
      campaignAssociation
    ),
  },
});

export const getSubscriptionPurchaseAnalyticsEvent = (
  configuredOffer: ConfiguredOffer,
  orderId: string
): EcommercePurchaseEventV4 => {
  const { selectedCommercialProducts, offer } = configuredOffer;
  // There is no possibility to purchase different multiple different subscriptions at once, so the commercial
  // products and addons are same in all objects. Just pick the first one in the array.
  const selectedCommercialProduct = selectedCommercialProducts[0];
  const { addedAddOns, selectedCampaignAssociation } = selectedCommercialProduct;
  const coupon = configuredOffer.voucher;
  const quantity = (selectedCommercialProducts && selectedCommercialProducts.length) || 1;
  const eCommerceItems: EcommerceItem[] = getEcommerceItems(
    configuredOffer.onlineModelName ?? '',
    offer,
    quantity,
    EcommerceEventTypeV4.PURCHASE,
    addedAddOns,
    coupon,
    selectedCampaignAssociation,
    // Just pick the first one in the array, since all items are same. If all are added, the price will be incorrectly multiplied.
    [selectedCommercialProduct]
  );
  const cumulativePrice = getCumulativePrice(eCommerceItems);

  return {
    event: EcommerceEventTypeV4.PURCHASE,
    payment_type: DEFAULT_PAYMENT_TYPE,
    ecommerce: {
      transaction_id: orderId,
      currency: CURRENCY_CODE_EUR,
      // cumulativePrice is already in euros
      value: cumulativePrice,
      tax: calculateAnalyticsTax(cumulativePrice),
      items: eCommerceItems,
      coupon,
      shipping: 0,
    },
  };
};

export const getKukaSoittaaOrderAnalyticsEvent = (quantity: number, monthlyPrice: number, transactionId: string) => {
  const eCommerceItems: EcommerceItem[] = [
    {
      item_id: '27df8d81-b6f4-0aeb-b0fe-0a841a4622d0',
      item_name: 'Elisa Kuka Soittaa',
      item_category: 'Additional Services',
      item_brand: 'Elisa',
      price: monthlyPrice * 12,
      quantity: quantity,
    },
  ];
  const cumulativePrice = getCumulativePrice(eCommerceItems);
  return {
    event: EcommerceEventTypeV4.PURCHASE,
    payment_type: DEFAULT_PAYMENT_TYPE,
    order_type: 'mass_order',
    ecommerce: {
      transaction_id: transactionId,
      currency: CURRENCY_CODE_EUR,
      value: cumulativePrice,
      items: eCommerceItems,
      tax: calculateAnalyticsTax(cumulativePrice),
    },
  };
};

const getSubscriptionAnalyticsItemBaseFromOffer = (onlineModelName: string, offer: Offer) => {
  const commercialProduct = getCommercialProductToUse(offer);
  const itemCategory = getItemCategory(
    onlineModelName,
    commercialProduct.productType,
    commercialProduct.productSubType
  );
  const subscriptionCard = getSubscriptionCardByOffer(offer, commercialProduct, itemCategory);
  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    item_name: subscriptionCard?.mainHeader ?? commercialProduct.commercialProductName,
    item_category: itemCategory,
  };
};

export const getOldSubscriptionAnalyticsItem = (onlineModelName: string, subscription: Subscription, offer?: Offer) => {
  const price = centsToEuros(12 * (subscription.details?.monthlyRecurringCharge ?? 0));
  const contractTerm = subscription.details?.contractTerm ?? subscription.details?.commitmentPeriod;
  const contractType = contractTerm ? `${contractTerm} MONTH` : 'OPEN-ENDED';
  if (!offer) {
    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      item_name: subscription.subscriptionName,
      item_category: subscription.subscriptionSubType ?? subscription.subscriptionType,
      price: price,
      contract_type: contractType,
    };
  }
  return {
    ...getSubscriptionAnalyticsItemBaseFromOffer(onlineModelName, offer),
    price: price,
    contract_type: contractType,
  };
};

export const getNewSubscriptionAnalyticsItem = (
  onlineModelName: string,
  offer: Offer,
  campaignAssociation?: CampaignAssociation,
  coupon?: string
) => {
  const commercialProduct = getCommercialProductToUse(offer);
  const isMobileBroadband = commercialProduct.productSubType === CommercialProductSubType.MOBILE_BROADBAND;
  const hasFixedTerm = !isMobileBroadband && commercialProduct.payments;
  const contractType = hasFixedTerm ? `${commercialProduct.payments} MONTH` : 'OPEN-ENDED';
  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    ...getSubscriptionAnalyticsItemBaseFromOffer(onlineModelName, offer),
    price: centsToEuros(
      getCommercialProductYearlyPriceWithoutOneTimeChargeInCents(
        [commercialProduct],
        campaignAssociation,
        isLaitenettiOffer(offer)
      )
    ),
    contract_type: contractType,
    coupon,
  };
};

export const getSubscriptionChangeAnalyticsEvent = (
  onlineModelName: string,
  subscription: Subscription,
  offer: Offer,
  previousOffer?: Offer,
  campaignAssociation?: CampaignAssociation,
  coupon?: string
) => {
  /* eslint-disable @typescript-eslint/naming-convention */
  return {
    event: 'subscription_change',
    previous_subscription: getOldSubscriptionAnalyticsItem(onlineModelName, subscription, previousOffer),
    new_subscription: getNewSubscriptionAnalyticsItem(onlineModelName, offer, campaignAssociation, coupon),
  };
};
